import React from 'react';
import { motion } from 'motion/react';
import { Box, Pencil, Shield, Music, RefreshCw, Zap, Wind } from 'lucide-react';
import { SimulationConfig, SimulationMode } from '@/types';
import { Toggle } from './Toggle';
import { InfoPopup } from './InfoPopup';

import { CollapsibleSection } from './CollapsibleSection';

interface ModeSelectorSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  isLocked?: boolean;
  minPlan?: string;
  onUpgradeClick?: () => void;
}

const MODE_ICONS: Record<string, any> = {
  SPAWNER: Box,
  DRAWER: Pencil,
  ESCAPE: Shield,
  RHYTHMIC: Music,
  SPACE_SPIRAL: RefreshCw,
};

export const ModeSelectorSection: React.FC<ModeSelectorSectionProps> = ({ 
    config, setConfig, isLocked, minPlan = "FREE", onUpgradeClick 
}) => {
  const modes: SimulationMode[] = [
    'RHYTHMIC',
    'ESCAPE',
    'DRAWER',
    'SPAWNER',
    ...(import.meta.env.DEV ? ['SPACE_SPIRAL'] : [])
  ] as SimulationMode[];
  
  const toggleFeature = (feature: keyof SimulationConfig) => {
    setConfig(p => ({ ...p, [feature]: !p[feature] }));
  };

  return (
    <CollapsibleSection 
        title="Core Logic" 
        icon={Zap} 
        darkMode={config.darkMode}
        settingKey="coreLogic"
        isLocked={isLocked} 
        lockRequiredPlan={minPlan as any}
        onUpgradeClick={onUpgradeClick}
        defaultOpen
    >
        <div className="flex flex-col gap-6 mt-4">
            {/* Simulation Architecture Grid */}
            <div className={`flex flex-col gap-2 ${isLocked ? 'opacity-50' : ''}`}>
                <div className="flex items-center justify-between px-1">
                    <span className="text-[10px] font-medium uppercase tracking-[0.2em] opacity-40">Simulation Type</span>
                    {isLocked && (
                        <button onClick={onUpgradeClick} className="text-[10px] font-medium text-amber-500 uppercase tracking-tighter hover:underline">
                            Upgrade to Unlock
                        </button>
                    )}
                    <InfoPopup settingKey="mode" darkMode={config.darkMode} />
                </div>
                <div className={`grid ${modes.length === 5 ? 'grid-cols-5' : 'grid-cols-4'} gap-1.5 w-full ${isLocked ? 'pointer-events-none' : ''}`}>
                    {modes.map(m => {
                        const Icon = MODE_ICONS[m] || Box;
                        const isActive = config.mode === m;
                        return (
                            <button 
                                key={m} 
                                onClick={() => setConfig(prev => {
                                    const nextMode = m as SimulationMode;
                                    return { 
                                        ...prev, 
                                        mode: nextMode,
                                        enableSpawner: nextMode === 'SPAWNER',
                                        enableDrawer: nextMode === 'DRAWER',
                                        enableEscape: nextMode === 'ESCAPE' || nextMode === 'RHYTHMIC_ESCAPE',
                                        enableRhythmic: nextMode === 'RHYTHMIC' || nextMode === 'RHYTHMIC_ESCAPE',
                                        enableSpaceSpiral: nextMode === 'SPACE_SPIRAL',
                                        initialBalls: nextMode === 'SPAWNER' ? 2 : 1
                                    };
                                })} 
                                className={`group relative flex flex-col items-center justify-center py-4 rounded-xl transition-all duration-300 ${isActive ? (config.darkMode ? 'text-cyan-400' : 'text-cyan-600') : 'text-zinc-400 hover:text-zinc-500'}`}
                                title={m.replace('_', ' ')}
                            >
                                {isActive && (
                                    <motion.div 
                                        layoutId="activeMode" 
                                        className={`absolute inset-0 z-0 rounded-xl border ${config.darkMode ? 'border-cyan-400/40 bg-cyan-400/10 shadow-[0_0_15px_rgba(34,211,238,0.1)]' : 'border-cyan-600/30 bg-cyan-50 shadow-sm'}`} 
                                    />
                                )}
                                <Icon className={`w-3.5 h-3.5 mb-2 relative z-10 transition-transform duration-300 group-hover:scale-110 ${isActive ? (config.darkMode ? 'text-cyan-400' : 'text-cyan-600') : 'text-zinc-200'}`} />
                                <span className="text-[8px] font-medium uppercase tracking-widest relative z-10 truncate w-full text-center px-1">
                                    {m === 'SPACE_SPIRAL' ? 'Spiral' : m.replace('_', ' ')}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Engine Modifiers & Hybrids */}
            <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between px-1">
                    <span className="text-[10px] font-medium uppercase tracking-[0.2em] opacity-40">Add-ons</span>
                </div>
                <div className="grid grid-cols-1 gap-1 w-full">
                    <ModifierRow 
                        icon={<Wind className="w-3.5 h-3.5 text-purple-500" />}
                        label="Line Trail Engine"
                        description="Smooth paths & trails"
                        active={config.enableTrails || config.trailEnabled}
                        onToggle={() => {
                            const nextActive = !(config.enableTrails || config.trailEnabled);
                            setConfig(p => ({ ...p, enableTrails: nextActive, trailEnabled: nextActive }));
                        }}
                        darkMode={config.darkMode}
                        settingKey="enableTrails"
                        isLocked={false} 
                    />
                    <ModifierRow 
                        icon={<Music className="w-3.5 h-3.5 text-pink-500" />}
                        label="Music Sync"
                        description="Visuals react to audio"
                        active={config.enableRhythmic}
                        onToggle={() => {
                            const nextEnableRhythmic = !config.enableRhythmic;
                            setConfig(p => {
                                let nextMode = p.mode;
                                if (nextEnableRhythmic && p.enableEscape) nextMode = 'RHYTHMIC_ESCAPE';
                                else if (nextEnableRhythmic) nextMode = 'RHYTHMIC';
                                else if (p.enableEscape) nextMode = 'ESCAPE';
                                
                                return { 
                                    ...p, 
                                    enableRhythmic: nextEnableRhythmic,
                                    mode: nextMode,
                                    initialBalls: (nextMode === 'SPAWNER') ? 2 : 1
                                };
                            });
                        }}
                        darkMode={config.darkMode}
                        settingKey="enableRhythmic"
                    />
                    <ModifierRow 
                        icon={<Shield className="w-3.5 h-3.5 text-amber-500" />}
                        label="Breaking Rings"
                        description="Escape through rings"
                        active={config.enableEscape}
                        onToggle={() => {
                            const nextEnableEscape = !config.enableEscape;
                            setConfig(p => {
                                let nextMode = p.mode;
                                if (nextEnableEscape && p.enableRhythmic) nextMode = 'RHYTHMIC_ESCAPE';
                                else if (nextEnableEscape) nextMode = 'ESCAPE';
                                else if (p.enableRhythmic) nextMode = 'RHYTHMIC';
                                
                                return { 
                                    ...p, 
                                    enableEscape: nextEnableEscape,
                                    mode: nextMode,
                                    initialBalls: (nextMode === 'SPAWNER') ? 2 : 1
                                };
                            });
                        }}
                        darkMode={config.darkMode}
                        settingKey="enableEscape"
                    />
                </div>
            </div>
        </div>
    </CollapsibleSection>
  );
};

const ModifierRow = ({ icon, label, description, active, onToggle, darkMode, settingKey, isLocked }: any) => (
    <div 
        className={`flex items-center justify-between p-3 rounded-[1.5rem] transition-all duration-300 border ${active ? (darkMode ? 'bg-white/10 border-white/10' : 'bg-white border-zinc-950/10 shadow-sm') : 'bg-transparent border-transparent hover:bg-black/5 opacity-60 hover:opacity-100'} ${isLocked ? 'opacity-40 pointer-events-none' : ''}`}
    >
        <div className="flex items-center gap-3 flex-1" onClick={() => !isLocked && onToggle()} style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}>
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${darkMode ? 'bg-white/5' : 'bg-zinc-100'}`}>
                {icon}
            </div>
            <div className="flex flex-col items-start">
                <div className="flex items-center gap-2">
                    <span className="text-[10px] font-medium uppercase tracking-widest leading-none">{label}</span>
                    <InfoPopup settingKey={settingKey} darkMode={darkMode} />
                </div>
                <span className="text-[8px] font-medium opacity-30 uppercase tracking-widest mt-1 truncate max-w-[120px]">{description}</span>
            </div>
        </div>
        <Toggle active={active} onClick={(e?: React.MouseEvent) => { e?.stopPropagation(); onToggle(); }} />
    </div>
);
